import { v4 as uuidv4 } from 'uuid';
import { 
  FinancialRecord, 
  FinancialRecordFilter, 
  FinancialRecordsResponse,
  FinancialSummary
} from '../types/data/financialRecord';
import axios from 'axios';

// API base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class FinancialRecordService {
  // Get all financial records with optional filtering
  async getFinancialRecords(
    filter?: FinancialRecordFilter,
    page: number = 1,
    pageSize: number = 20,
    calculateSummary: boolean = false
  ): Promise<FinancialRecordsResponse> {
    try {
      console.log('DEBUG SERVICE: Fetching financial records from backend - API Call: GET /api/financial-records');
      
      // Build query parameters
      const params: Record<string, string | number | boolean> = {
        page,
        pageSize,
        calculateSummary
      };
      
      // Add filter parameters if they exist
      if (filter) {
        if (filter.brands && filter.brands.length > 0) params.brands = filter.brands.join(',');
        if (filter.dealers && filter.dealers.length > 0) params.dealers = filter.dealers.join(',');
        if (filter.years && filter.years.length > 0) params.years = filter.years.join(',');
        if (filter.months && filter.months.length > 0) params.months = filter.months.join(',');
        if (filter.kpiCodes && filter.kpiCodes.length > 0) params.kpiCodes = filter.kpiCodes.join(',');
        if (filter.kpiCategories && filter.kpiCategories.length > 0) params.kpiCategories = filter.kpiCategories.join(',');
        if (filter.page) params.page = filter.page;
        if (filter.limit) params.limit = filter.limit;
        if (filter.sortBy) params.sortBy = filter.sortBy;
        if (filter.sortOrder) params.sortOrder = filter.sortOrder;
      }
      
      console.log('DEBUG SERVICE: Request params:', params);
      
      // Make the API call
      const response = await axios.get(`${API_BASE_URL}/financial-records`, { params });
      
      console.log(`DEBUG SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG SERVICE: Fetched ${response.data.records?.length || 0} records out of ${response.data.totalCount || 0} total`);
      
      return response.data;
    } catch (error) {
      // Create a more descriptive error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isNetworkError = errorMessage.includes('Network Error');
      
      console.error('Error fetching financial records from backend:', error);
      console.error(`API Error Details: ${isNetworkError ? 'Server may not be running' : errorMessage}`);
      
      // Throw the error to be handled by the store
      throw new Error(
        isNetworkError 
          ? 'Cannot connect to the server. Please ensure the server is running.' 
          : `Failed to fetch financial records: ${errorMessage}`
      );
    }
  }

  // Get a single financial record by ID
  async getFinancialRecordById(id: string): Promise<FinancialRecord | null> {
    try {
      console.log(`DEBUG SERVICE: Fetching financial record by ID ${id} - API Call: GET /api/financial-records/${id}`);
      
      const response = await axios.get(`${API_BASE_URL}/financial-records/${id}`);
      
      console.log(`DEBUG SERVICE: API Response Status: ${response.status}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching financial record with ID ${id}:`, error);
      return null;
    }
  }

  // Create a new financial record
  async createFinancialRecord(record: Omit<FinancialRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<FinancialRecord> {
    try {
      console.log('DEBUG SERVICE: Creating new financial record - API Call: POST /api/financial-records');
      console.log('DEBUG SERVICE: Request payload:', JSON.stringify(record, null, 2));
      
      const response = await axios.post(`${API_BASE_URL}/financial-records`, record);
      
      console.log(`DEBUG SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG SERVICE: Created record with ID: ${response.data.id}`);
      
      return response.data;
    } catch (error) {
      console.error('Error creating financial record:', error);
      throw error;
    }
  }

  // Update an existing financial record
  async updateFinancialRecord(id: string, updates: Partial<FinancialRecord>): Promise<FinancialRecord | null> {
    try {
      console.log(`DEBUG SERVICE: Updating financial record ${id} - API Call: PUT /api/financial-records/${id}`);
      console.log('DEBUG SERVICE: Update payload:', JSON.stringify(updates, null, 2));
      
      const response = await axios.put(`${API_BASE_URL}/financial-records/${id}`, updates);
      
      console.log(`DEBUG SERVICE: API Response Status: ${response.status}`);
      return response.data;
    } catch (error) {
      console.error(`Error updating financial record with ID ${id}:`, error);
      return null;
    }
  }

  // Delete a financial record
  async deleteFinancialRecord(id: string): Promise<boolean> {
    try {
      console.log(`DEBUG SERVICE: Deleting financial record ${id} - API Call: DELETE /api/financial-records/${id}`);
      
      const response = await axios.delete(`${API_BASE_URL}/financial-records/${id}`);
      
      console.log(`DEBUG SERVICE: API Response Status: ${response.status}`);
      return true;
    } catch (error) {
      console.error(`Error deleting financial record with ID ${id}:`, error);
      return false;
    }
  }

  // Get all financial records without pagination (for duplication checking)
  async getAllFinancialRecords(): Promise<FinancialRecord[]> {
    try {
      console.log('DEBUG SERVICE: Getting all financial records for duplication checking');
      
      // Set a large pageSize to get all records
      const response = await this.getFinancialRecords(undefined, 1, 1000, false);
      return response.records;
    } catch (error) {
      console.error('Error getting all financial records:', error);
      return [];
    }
  }
}

export default new FinancialRecordService(); 